import getBreaksSummary from '@sdk/automatedBreaks/helpers/getBreaksSummary';
import {
  canTakeMealBreak,
  canTakeRestBreak,
  shouldApplyAutomatedBreaksForShift,
} from '@sdk/automatedBreaks/utils';
import type { Shift } from '@sdk/automatedBreaks/types/Shift';
import { AutomatedBreaksSettingsUI } from '@sdk/automatedBreaks/types/AutomatedBreaksSettingsUI';

export type BreakAllowed = {
  isAllowed: boolean;
  meal?: boolean;
  rest?: boolean;
};

type Props = {
  automatedBreaksFeatureFlag: boolean;
  automatedBreaksSettings: AutomatedBreaksSettingsUI;
  shift: Shift;
};

const canTakeBreak = ({
  automatedBreaksFeatureFlag,
  automatedBreaksSettings,
  shift,
}: Props): BreakAllowed => {
  const isClockedIn = shift.clockInStatus.clockedIn && !shift.clockInStatus.onBreak;

  if (!isClockedIn) {
    return {
      isAllowed: false,
    };
  }

  const automatedBreaksApply = shouldApplyAutomatedBreaksForShift({
    shift,
    isAutomatedBreakEnabled: automatedBreaksFeatureFlag && automatedBreaksSettings.isEnabled,
  });

  if (!automatedBreaksApply) {
    // Automated Breaks disabled: same logic that has been present before automated breaks.
    return {
      isAllowed: Boolean(shift.clockInStatus.eligibleForBreak),
    };
  }

  // Automated breaks are enabled
  const { mealBreaksTaken, restBreaksTaken } = getBreaksSummary(
    shift.timesheetEntries,
    shift.dtstart,
    shift.dtend
  );

  const mealBreakAllowed = canTakeMealBreak(
    shift,
    automatedBreaksSettings,
    mealBreaksTaken
  ).isAllowed;
  const restBreakAllowed = canTakeRestBreak(
    shift,
    automatedBreaksSettings,
    restBreaksTaken
  ).isAllowed;

  return {
    isAllowed: mealBreakAllowed || restBreakAllowed,
    meal: mealBreakAllowed,
    rest: restBreakAllowed,
  };
};

export default canTakeBreak;
